/**
 * The website row's power actions — the first fetch, the P3 extractors and re-fetch, on /deck.
 *
 * A port of the canvas's `useBirtvyWebIngest` with the imperative canvas API swapped for the blob:
 * same routes, same filters, same layout math, but every write is a `mutate` read-modify-write and
 * every async patch RE-FINDS its card by id AND url before touching it — the blob can change under
 * any await (the canvas in a second tab, another action here), and a freed id can be reused by a
 * later create (`nextBlobId` is max+1).
 *
 * DELTAS FROM THE CANVAS, all deliberate:
 * - Extract text ALWAYS lands as a DOCUMENT. Notes have no directory kind, so a note here would be
 *   a success the user cannot see. Short post text still deserves a row.
 * - Placement is `placeIn` (right of everything) — this workspace is a list, not a camera, so the
 *   canvas's place-next-to-the-card rule has no viewport to negotiate with. The extracted-images
 *   BLOCK keeps the canvas's wrapped-row layout inside the spot it is given.
 * - Feedback goes through `notify`: the row wears `updating` while a job runs, the result flashes
 *   on the row's meta line, and only failures toast.
 * - One job per card at a time (the canvas's busy latch), tracked in `beginRowWork`.
 * - `resolve` is the canvas's INGEST, which /deck did not have as an action at all: the first fetch
 *   used to be welded into `+ website`, so the card only came into being once the network had
 *   answered. It is an action on an existing card now, and the create is just a create.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { BrandStyle } from '~/design-system/components/DeckGen/types/presentation';
import { DOC_H, DOC_W, type BirtvyDoc } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import { imageInsertSize, type BirtvyImage } from '~/design-system/components/DeckGen/birtvy/birtvyImage';
import { createDocument as createLibraryDocument } from '~/design-system/components/DeckGen/birtvy/birtvyDocSync';
import { brandStyleFromScreen } from '~/design-system/components/DeckGen/birtvy/web/birtvyBrandTruth';
import {
  buildBrandStyleTiles,
  hostnameOf,
  isSocialPostUrl,
  readableFromScrapedMarkdown,
  SOCIAL_CARD_SIZE,
  WEB_EXTRACT_GAP,
  WEB_EXTRACT_IMAGE_GAP,
  type BirtvyWeb,
} from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';
import { measureImageUrl, toWebHref, upscaleImageUrl } from '~/design-system/components/DeckGen/utils/imageElement';
import type { DeckBlob } from '../deckContent';
import { itemKey } from '../ids';
import { fetchWebPreview } from './webPreview';
import type { DeckBlobState } from '../useDeckBlob';
import { beginRowWork, endRowWork, flashRow, toastError } from './notify';
import { nextBlobId } from './toDirectoryItems';
import { placeIn } from './useDeckCreate';

/** The canvas's extraction filters, verbatim. */
const MIN_EXTRACT_LONG_SIDE = 400;
const MIN_EXTRACT_SHORT_SIDE = 200;
const MAX_EXTRACT_IMAGES = 24;

export interface WebActions {
  /**
   * Give a card its address and fetch what is behind it. The blank-card half of `+ website`; a card
   * that already has a url takes `refetch` instead, which asks the same two endpoints without moving
   * the address.
   */
  resolve: (web: BirtvyWeb, url: string) => void;
  refetch: (web: BirtvyWeb) => void;
  extractText: (web: BirtvyWeb) => void;
  extractImages: (web: BirtvyWeb) => void;
  extractStyles: (web: BirtvyWeb) => void;
}

export interface WebActionsOpts {
  /**
   * An extraction produced a directory item — selection follows action: the host jumps to it. Fired
   * with the new document's key, or the FIRST of an image batch (the batch is one block; landing on
   * its first row shows the whole thing arriving).
   */
  onProduced?: (key: string) => void;
}

/** Patch ONE site card, matched by id + canonical url — never by id alone across an await. */
function patchWebIn(blob: DeckBlob, id: number, url: string, patch: Partial<BirtvyWeb>): DeckBlob {
  return {
    ...blob,
    webs: (blob.webs ?? []).map((w) =>
      w.id === id && (w.kind === 'web' || w.kind === undefined) && toWebHref(w.url) === toWebHref(url)
        ? { ...w, ...patch }
        : w,
    ),
  };
}

// ── The intake seam ──────────────────────────────────────────────────────────────────────────────────

/**
 * How the preview's URL field reaches `resolve`.
 *
 * The control that asks for the address is drawn by `stageFor` — a PURE function of the entity, called
 * in the host's render body — so it holds neither `mutate` nor a conversation id, and there is no prop
 * route from this hook to it. The function travels the other way instead, exactly as `docEditBus`
 * carries the document editor's save: one module-level slot, written by the hook that owns the action.
 *
 * Registered by the hook itself rather than by `DeckWorkspace`, which is the one difference from
 * `docEditBus`. That bus is registered by the workspace because it is CONDITIONAL — only a writable
 * blob may register a save, and only the workspace knows. There is no such gate here: wherever
 * `useWebActions` is mounted, resolve is exactly as available as `refetch` is, and asking the surface
 * to re-publish it would be a second copy of a fact this file already holds.
 *
 * Unregistered = the pill submits into nothing, never a crash — the same contract the doc bus keeps,
 * for the same reason: the stage renders before the hook's effect runs, and can outlive it.
 */
type WebIntakeResolve = (web: BirtvyWeb, url: string) => void;
let intake: WebIntakeResolve | null = null;

/** Hand a blank card its address. Called by `preview/WebsiteIntake`; a no-op with nothing registered. */
export function resolveWebIntake(web: BirtvyWeb, url: string): void {
  intake?.(web, url);
}

export function useWebActions(
  conversationId: string | undefined,
  mutate: DeckBlobState['mutate'],
  opts?: WebActionsOpts,
): WebActions {
  /** The one-job-per-card latch. A card mid-extract refuses a second press quietly. */
  const busy = useRef(new Set<number>());
  /** Latest-value ref, so the memoised jobs never fire a stale host callback. */
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const run = useCallback(
    (web: BirtvyWeb, label: string, job: () => Promise<void>) => {
      if (!conversationId || busy.current.has(web.id)) return;
      busy.current.add(web.id);
      const key = itemKey('websites', web.id);
      beginRowWork(key, label);
      void job()
        .catch(() => toastError('Something went wrong on this website'))
        .finally(() => {
          busy.current.delete(web.id);
          endRowWork(key);
        });
    },
    [conversationId],
  );

  // ── Resolve: a blank card's first fetch ────────────────────────────────────────────────────────────

  /**
   * The address, then the page behind it, onto a card that already exists.
   *
   * This is `createWebsiteCard`'s old body with the create taken out of it, and the difference is the
   * whole point of the change: the card is no longer a REWARD for a successful fetch. It is already in
   * the deck, already selected, and this only fills it in.
   *
   * TWO WRITES, AND THE SECOND CANNOT REUSE THE FIRST'S MATCH. `patchWebIn` finds its card by id AND
   * canonical url, and this is the one action that MOVES the url — so the first patch matches on the
   * card's current address (`''` for a blank card, which canonicalises to `https://` on both sides of
   * the comparison, so it matches) and every patch after it matches on the address just written.
   * Matching the result patch on `web.url` again would silently write nothing.
   *
   * The address lands before the network is asked, with `status: 'loading'`. That is what takes the
   * intake off the stage and puts the pane's own thinking pill there instead — without it the field
   * sits there through the whole fetch looking untouched, and the busy latch below is the only thing
   * stopping a second submit.
   *
   * The social fork is the canvas's (`useBirtvyWebIngest`) and `refetch`'s: x.com and instagram.com
   * block iframing AND screenshotting, so those go to the extractor and the card renders the POST.
   * Without the fork an x.com link comes back with no screenshot and lands `offline`, which is not
   * what happened.
   *
   * A FAILURE IS A CARD, NOT A TOAST. The throw is swallowed and the card lands in `error` — the row
   * reads `offline`, the pane draws its failed board, and `⋯ → Re-fetch` is live because the card now
   * has a url. That is deliberately the old create's behaviour ("the card still gets made, in its
   * `error` state — the URL is what the user asked for"), and it is the reason this job never rejects:
   * `run`'s catch would put a toast over a state the row is already telling you about.
   */
  const resolve = useCallback(
    (web: BirtvyWeb, entered: string) => {
      // Nothing typed is not a submit. The pill's own button is disabled while the field is blank;
      // this is the Enter key, which no disabled attribute covers.
      if (!entered.trim()) return;
      const url = toWebHref(entered);
      run(web, 'updating · fetching', async () => {
        await mutate((blob) => patchWebIn(blob, web.id, web.url, { url, status: 'loading' }));
        const preview = await fetchWebPreview(conversationId!, entered);
        await mutate((blob) =>
          patchWebIn(blob, web.id, url, {
            ...preview.patch,
            // The card was minted at the site default because nothing knew which kind it was. A post
            // is laid out portrait, so it is re-shaped now that we do — the same correction `refetch`
            // makes when a site turns out to be one.
            ...(preview.patch.post ? { width: preview.size.width, height: preview.size.height } : {}),
          }),
        );
        flashRow(
          itemKey('websites', web.id),
          preview.ready ? 'fetched just now' : 'could not reach this page',
        );
      });
    },
    [conversationId, mutate, run],
  );

  /**
   * Publish it for the preview's intake. An effect rather than a render-body assignment so a second
   * mount cannot leave a torn-down hook's `mutate` in the slot, and the teardown clears only ITS OWN
   * registration — StrictMode runs register → unregister → register, and an unconditional clear would
   * kill the live one a frame after it landed.
   */
  useEffect(() => {
    intake = resolve;
    return () => {
      if (intake === resolve) intake = null;
    };
  }, [resolve]);

  // ── Re-fetch ───────────────────────────────────────────────────────────────────────────────────────

  const refetch = useCallback(
    (web: BirtvyWeb) =>
      run(web, 'updating · fetching again', async () => {
        const cid = encodeURIComponent(conversationId!);
        const url = toWebHref(web.url);

        if (isSocialPostUrl(url)) {
          // A working post routed to the screenshot endpoint would flip to `error` — the platforms
          // block both framing and shots. Same fork as ingest, always.
          const res = await fetch(`/conversation-social-extract-api/${cid}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url }),
          });
          if (!res.ok) throw new Error(`social-extract ${res.status}`);
          const data = (await res.json()) as {
            platform?: string;
            author?: string;
            authorAvatar?: string;
            title?: string;
            text?: string;
            images?: string[];
            videos?: { url: string; poster?: string; width?: number; height?: number }[];
            url?: string;
          };
          const text = data.text?.trim() ?? '';
          const images = data.images ?? [];
          if (!text && images.length === 0 && (data.videos?.length ?? 0) === 0) throw new Error('empty post');
          await mutate((blob) =>
            patchWebIn(blob, web.id, web.url, {
              status: 'ready',
              title: data.title ?? '',
              sourceImages: images,
              post: {
                platform: data.platform ?? 'social',
                author: data.author,
                authorAvatar: data.authorAvatar,
                text,
                images,
                videos: data.videos ?? [],
              },
              width: SOCIAL_CARD_SIZE.width,
              height: SOCIAL_CARD_SIZE.height,
              url: data.url ?? url,
              checkedAt: Date.now(),
            }),
          );
          flashRow(itemKey('websites', web.id), 'fetched just now');
          return;
        }

        const res = await fetch(`/conversation-web-preview-api/${cid}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        });
        if (!res.ok) throw new Error(`web-preview ${res.status}`);
        const data = (await res.json()) as {
          url?: string;
          title?: string;
          screenshotUrl?: string;
          images?: string[];
          frameable?: boolean;
        };
        if (!data.screenshotUrl) throw new Error('no screenshot');
        await mutate((blob) =>
          patchWebIn(blob, web.id, web.url, {
            status: 'ready',
            screenshotUrl: data.screenshotUrl,
            title: data.title ?? '',
            sourceImages: data.images ?? [],
            frameable: data.frameable ?? false,
            url: data.url ?? url,
            checkedAt: Date.now(),
          }),
        );
        flashRow(itemKey('websites', web.id), 'fetched just now');
      }),
    [conversationId, mutate, run],
  );

  // ── Extract text → a document ──────────────────────────────────────────────────────────────────────

  const extractText = useCallback(
    (web: BirtvyWeb) =>
      run(web, 'updating · extracting text', async () => {
        const cid = encodeURIComponent(conversationId!);
        const res = await fetch(`/conversation-social-extract-api/${cid}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: web.url }),
        });
        if (!res.ok) throw new Error(`social-extract ${res.status}`);
        const post = (await res.json()) as { text?: string };
        // A social post's text is already clean; a scraped SITE arrives as whole-page markdown — strip
        // the nav/CTA/footer chrome or the document opens on a link list.
        const text = web.post ? post.text?.trim() : readableFromScrapedMarkdown(post.text ?? '');
        if (!text) {
          flashRow(itemKey('websites', web.id), 'nothing readable on this page');
          return;
        }
        // The Library ORIGINAL first, then the card that references it — `useDeckCreate`'s split. The
        // page title beats the first scraped line, which is a breadcrumb more often than a headline.
        const title = web.title?.trim() || hostnameOf(web.url);
        const row = await createLibraryDocument(conversationId!, { content: text, title });
        if (!row) throw new Error('library create failed');
        const minted: { id: number | null } = { id: null };
        await mutate((blob) => {
          const id = nextBlobId(blob);
          const { x, y } = placeIn(blob, { width: DOC_W, height: DOC_H });
          const doc: BirtvyDoc = {
            id,
            x,
            y,
            width: DOC_W,
            height: DOC_H,
            content: row.content ?? text,
            title: row.title ?? title,
            documentId: row.id,
            groupId: null,
          };
          minted.id = id;
          return { ...blob, docs: [...(blob.docs ?? []), doc] };
        });
        flashRow(itemKey('websites', web.id), 'text extracted');
        if (minted.id !== null) optsRef.current?.onProduced?.(itemKey('documents', minted.id));
      }),
    [conversationId, mutate, run],
  );

  // ── Extract images ─────────────────────────────────────────────────────────────────────────────────

  const extractImages = useCallback(
    (web: BirtvyWeb) =>
      run(web, 'updating · extracting images', async () => {
        const cid = encodeURIComponent(conversationId!);

        const storeAndMeasure = async (
          url: string,
        ): Promise<{ url: string; width: number; height: number } | null> => {
          try {
            const res = await fetch(`/conversation-images-api/${cid}/from-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url }),
            });
            if (!res.ok) return null;
            const data = (await res.json()) as { url?: string };
            if (!data.url) return null;
            const dim = await measureImageUrl(data.url);
            if (!dim) return null;
            return { url: data.url, width: dim.width, height: dim.height };
          } catch {
            return null;
          }
        };

        // The canvas's exact selection: posts take every carousel image, sites keep only the big ones.
        let placeable: { url: string; width: number; height: number }[];
        if (web.post) {
          const urls = [...new Set(web.post.images ?? [])].slice(0, MAX_EXTRACT_IMAGES);
          const stored = await Promise.all(urls.map(storeAndMeasure));
          placeable = stored.filter((s): s is NonNullable<typeof s> => s != null);
        } else {
          const candidates = [...new Set((web.sourceImages ?? []).map(upscaleImageUrl))];
          const measured = await Promise.all(
            candidates.map(async (url) => ({ url, dim: await measureImageUrl(url) })),
          );
          const kept = measured
            .filter(
              (m): m is { url: string; dim: { width: number; height: number } } =>
                m.dim != null &&
                Math.max(m.dim.width, m.dim.height) >= MIN_EXTRACT_LONG_SIDE &&
                Math.min(m.dim.width, m.dim.height) >= MIN_EXTRACT_SHORT_SIDE,
            )
            .slice(0, MAX_EXTRACT_IMAGES);
          const stored = await Promise.all(kept.map((m) => storeAndMeasure(m.url)));
          placeable = stored.filter((s): s is NonNullable<typeof s> => s != null);
        }

        if (placeable.length === 0) {
          flashRow(itemKey('websites', web.id), 'no images worth keeping');
          return;
        }

        // The canvas's wrapped-row block layout, laid in block-local coords, landed in ONE spot, in
        // ONE mutate — five racing writes on a 600ms-debounced whole-blob save is how images vanish.
        const maxRowWidth = 2400;
        const laid: {
          img: { url: string; width: number; height: number };
          box: { width: number; height: number };
          dx: number;
          dy: number;
        }[] = [];
        let cursorX = 0;
        let rowY = 0;
        let rowMaxH = 0;
        let blockW = 0;
        for (const img of placeable) {
          const box = imageInsertSize(img.width, img.height);
          if (cursorX > 0 && cursorX + box.width > maxRowWidth) {
            cursorX = 0;
            rowY += rowMaxH + WEB_EXTRACT_IMAGE_GAP;
            rowMaxH = 0;
          }
          laid.push({ img, box, dx: cursorX, dy: rowY });
          cursorX += box.width + WEB_EXTRACT_IMAGE_GAP;
          rowMaxH = Math.max(rowMaxH, box.height);
          blockW = Math.max(blockW, cursorX - WEB_EXTRACT_IMAGE_GAP);
        }
        const blockH = rowY + rowMaxH;

        const minted: { first: number | null } = { first: null };
        await mutate((blob) => {
          const origin = placeIn(blob, {
            width: blockW + WEB_EXTRACT_GAP * 2,
            height: blockH + WEB_EXTRACT_GAP * 2,
          });
          let id = nextBlobId(blob);
          minted.first = id;
          const cards: BirtvyImage[] = laid.map(({ img, box, dx, dy }) => {
            const card: BirtvyImage = {
              id,
              x: origin.x + WEB_EXTRACT_GAP + dx,
              y: origin.y + WEB_EXTRACT_GAP + dy,
              width: box.width,
              height: box.height,
              src: img.url,
              sourceUrl: web.url,
              groupId: null,
            };
            id += 1;
            return card;
          });
          return { ...blob, images: [...(blob.images ?? []), ...cards] };
        });
        flashRow(
          itemKey('websites', web.id),
          `${placeable.length} image${placeable.length === 1 ? '' : 's'} extracted`,
        );
        if (minted.first !== null) optsRef.current?.onProduced?.(itemKey('images', minted.first));
      }),
    [conversationId, mutate, run],
  );

  // ── Extract styles ─────────────────────────────────────────────────────────────────────────────────

  const extractStyles = useCallback(
    (web: BirtvyWeb) =>
      run(web, 'updating · extracting styles', async () => {
        const cid = encodeURIComponent(conversationId!);
        let style: BrandStyle | undefined = web.brandStyle;
        if (!style) {
          const res = await fetch(`/conversation-web-style-api/${cid}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: web.url }),
          });
          if (res.ok) style = (await res.json()) as BrandStyle;
        }
        if (!style || (!style.palette?.length && Object.keys(style.roles ?? {}).length === 0)) {
          flashRow(itemKey('websites', web.id), 'no brand style found');
          return;
        }
        // Ground the palette in the RENDERED page before it becomes tiles — the CSS-declaration
        // ranking cannot tell a colour the site paints from one its framework merely ships.
        const grounded = await brandStyleFromScreen(style, web.screenshotUrl);
        await mutate((blob) => {
          const source = (blob.webs ?? []).find(
            (w) => w.id === web.id && toWebHref(w.url) === toWebHref(web.url),
          );
          if (!source) return blob; // deleted mid-extract — nothing to hang tiles off
          let id = nextBlobId(blob);
          const tiles = buildBrandStyleTiles(
            { x: source.x, y: source.y + source.height + WEB_EXTRACT_GAP },
            grounded,
            () => id++,
          );
          const stashed = patchWebIn(blob, web.id, web.url, { brandStyle: grounded });
          return { ...stashed, webs: [...(stashed.webs ?? []), ...tiles] };
        });
        // The tiles are real but this directory filters them out (they are canvas furniture, not
        // sites) — say so, or the success is invisible.
        flashRow(itemKey('websites', web.id), 'styles extracted, tiles are on the deck');
      }),
    [conversationId, mutate, run],
  );

  return { resolve, refetch, extractText, extractImages, extractStyles };
}
